import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';

export default function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-raspberry-800"
            data-testid="disclaimer-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-amber-700" />
          </div>
          <p className="text-xs font-bold tracking-widest uppercase text-raspberry-800">Medical Disclaimer</p>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-3">
          Gynovate AI/SI – Medical Disclaimer
        </h1>
        <p className="text-xs text-slate-500 mb-10">Effective Date: 1 March 2026</p>

        <div className="prose prose-slate max-w-none text-sm sm:text-base leading-relaxed text-slate-700 space-y-6">
          <p className="text-base">
            Gynovate AI/SI is a technology platform that provides AI-generated clinical support content for
            qualified healthcare professionals. It is intended solely for educational, informational,
            and workflow assistance purposes.
          </p>
          <p className="font-semibold text-slate-900 bg-amber-50 border-l-4 border-amber-400 pl-4 py-3 rounded-r-md">
            Gynovate AI/SI does not provide medical advice, diagnosis, or treatment recommendations.
          </p>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">1. No Doctor–Patient Relationship</h2>
            <p>Use of Gynovate AI/SI does not establish any doctor–patient relationship between the platform and any user or patient.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">2. Clinical Responsibility</h2>
            <p>All outputs generated by Gynovate AI/SI must be independently reviewed, verified, and validated by a qualified and registered medical practitioner before use.</p>
            <p className="mt-2">The treating doctor retains full and sole responsibility for:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Diagnosis</li>
              <li>Treatment decisions</li>
              <li>Patient communication</li>
              <li>Clinical outcomes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">3. AI Limitations</h2>
            <p>Gynovate AI/SI may generate incomplete, inaccurate, or outdated information. The outputs are based on available inputs and do not account for complete patient history, examination findings, or real-time clinical judgment.</p>
            <p className="mt-2">The platform must not be relied upon as the sole basis for any clinical decision.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">4. Not for Emergency Use</h2>
            <p>Gynovate AI/SI is not intended for use in emergency, critical care, or life-threatening situations. Users must rely on established clinical protocols and professional judgment in such cases.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">5. Data Usage and Compliance</h2>
            <p>Users are responsible for ensuring that any data entered into the platform is used lawfully and with appropriate patient consent where applicable.</p>
            <p className="mt-2">Gynovate AI/SI follows applicable principles under the Digital Personal Data Protection Act.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">6. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law, Gynovate AI/SI, its founders, employees, and affiliates shall not be liable for:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Any clinical decisions made using the platform</li>
              <li>Errors or omissions in AI-generated outputs</li>
              <li>Any direct, indirect, incidental, or consequential harm arising from use of the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">7. Regulatory Position</h2>
            <p>Gynovate AI/SI is a clinical support tool and is not a registered medical device. Users are responsible for ensuring compliance with applicable regulations and professional guidelines, including those issued by the National Medical Commission.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">8. User Eligibility</h2>
            <p>This platform is intended only for qualified healthcare professionals. By using Gynovate AI/SI, you confirm that you are a registered medical practitioner and will use the platform within your professional scope.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">9. Acceptance</h2>
            <p>By accessing or using Gynovate AI/SI, you confirm that you have read, understood, and agreed to this disclaimer.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">10. No Warranty</h2>
            <p>Gynovate AI/SI is provided on an &ldquo;as-is&rdquo; and &ldquo;as-available&rdquo; basis without any warranties of any kind, whether express or implied. The platform does not guarantee:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Accuracy, completeness, or reliability of outputs</li>
              <li>Fitness for clinical or medical use</li>
              <li>Continuous availability or error-free performance</li>
            </ul>
            <p className="mt-2">Use of the platform is entirely at the user&rsquo;s own risk.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">11. Indemnification</h2>
            <p>By using Gynovate AI/SI, you agree to indemnify, defend, and hold harmless the platform, its founders, employees, and affiliates from and against any claims, liabilities, damages, losses, or expenses arising out of:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Use or misuse of the platform</li>
              <li>Reliance on AI-generated outputs</li>
              <li>Violation of applicable laws, regulations, or professional guidelines</li>
              <li>Any harm caused to patients or third parties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">12. Governing Law and Jurisdiction</h2>
            <p>This disclaimer shall be governed by and interpreted in accordance with the laws of India. Any disputes arising from the use of Gynovate AI/SI shall be subject to the exclusive jurisdiction of the courts of Goa.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">13. Data Responsibility and Patient Information</h2>
            <p>Users must ensure that:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>No identifiable patient data is entered without proper consent and legal compliance</li>
              <li>All data shared is handled in accordance with applicable privacy laws, including the Digital Personal Data Protection Act</li>
              <li>Gynovate AI/SI is not intended to function as a medical records storage system</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">14. Prohibited Use</h2>
            <p>Users agree not to use Gynovate AI/SI:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>For emergency or life-threatening medical situations</li>
              <li>As a substitute for professional clinical judgment</li>
              <li>For direct patient communication without review by a qualified doctor</li>
              <li>For medico-legal certification, prescriptions, or official medical documentation without verification</li>
              <li>If not a qualified and registered healthcare professional</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">15. No Clinical Decision Automation</h2>
            <p>Gynovate AI/SI is a support tool and must not be used to automate or replace clinical decision-making.</p>
            <p className="mt-2">All medical decisions must be made solely by a qualified healthcare professional based on independent judgment.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mt-8 mb-2">16. Usage Monitoring and Audit</h2>
            <p>Gynovate AI/SI reserves the right to monitor, log, and review platform usage for:</p>
            <ul className="list-disc pl-6 mt-1 space-y-0.5">
              <li>Security purposes</li>
              <li>Legal compliance</li>
              <li>Quality improvement</li>
            </ul>
            <p className="mt-2">Such records may be used in case of disputes or regulatory requirements.</p>
          </section>

          <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-3">
            <Link to="/privacy" className="text-raspberry-800 font-semibold hover:underline">Privacy Policy →</Link>
            <span>·</span>
            <Link to="/" className="text-raspberry-800 font-semibold hover:underline">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
